import { useMemo } from 'react';
import { scaleOrdinal } from 'd3-scale';
import { ForceGraphData, ForceGraphMapping, ForceNode, ForceLink } from '../config/forceGraphTypes';

export const useForceGraphData = (
    data: any[],
    mapping: ForceGraphMapping,
    palette?: string[]
): ForceGraphData => {
    return useMemo(() => {
        if (!data) return { nodes: [], links: [] };

        // Check if data is already processed (has nodes and links)
        if ('nodes' in data && 'links' in data) {
            return data as ForceGraphData;
        }

        if (!Array.isArray(data) || data.length === 0) return { nodes: [], links: [] };

        const nodesMap = new Map<string, ForceNode>();
        const links: ForceLink[] = [];

        const colorScale = palette && palette.length > 0 ? scaleOrdinal(palette) : null;

        data.forEach((row, index) => {
            // Extract Source Node
            const sourceId = String(row[mapping.linkSourceField]);
            if (!nodesMap.has(sourceId)) {
                // Priority: Group Field (mapped via palette) > Color Field (raw color) > Default
                const groupVal = mapping.nodeGroupField ? String(row[mapping.nodeGroupField]) : undefined;
                const rawColor = mapping.nodeColorField ? String(row[mapping.nodeColorField]) : undefined;

                let assignedColor: string | undefined = undefined;
                if (groupVal && colorScale) {
                    assignedColor = colorScale(groupVal) as string;
                } else if (rawColor) {
                    assignedColor = rawColor;
                }

                nodesMap.set(sourceId, {
                    id: sourceId,
                    label: mapping.nodeLabelField ? row[mapping.nodeLabelField] : sourceId,
                    group: groupVal,
                    value: mapping.nodeValueField ? parseFloat(row[mapping.nodeValueField]) : 1,
                    color: assignedColor,
                    ...row // Pass through all data for tooltips
                });
            }

            // Extract Target Node
            const targetId = String(row[mapping.linkTargetField]);
            if (!nodesMap.has(targetId)) {
                // Priority: Group Field (mapped via palette) > Color Field (raw color) > Default
                const groupVal = mapping.nodeGroupField ? String(row[mapping.nodeGroupField]) : undefined;
                const rawColor = mapping.nodeColorField ? String(row[mapping.nodeColorField]) : undefined;

                let assignedColor: string | undefined = undefined;
                if (groupVal && colorScale) {
                    assignedColor = colorScale(groupVal) as string;
                } else if (rawColor) {
                    assignedColor = rawColor;
                }

                nodesMap.set(targetId, {
                    id: targetId,
                    label: mapping.nodeLabelField ? row[mapping.nodeLabelField] : targetId,
                    group: groupVal,
                    value: 1, // Default value for targets if not specified
                    color: assignedColor,
                    ...row
                });
            }

            // Create Link
            links.push({
                id: `link-${index}`,
                source: sourceId,
                target: targetId,
                value: mapping.linkValueField ? parseFloat(row[mapping.linkValueField]) : 1,
                type: mapping.linkTypeField ? row[mapping.linkTypeField] : undefined,
                ...row
            });
        });

        return {
            nodes: Array.from(nodesMap.values()),
            links
        };
    }, [data, mapping, palette]);
};
